import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';

import footnoteDisclaimersText from '../../../../statics/screener/footnote-disclaimers';
import format from '../../../../utils/format';
import getRestrictionsText from '../../../../stores/screener/screener-redux-store/helpers/get-restriction-text';
import styles from './footer-disclaimers.module.scss';
import useScreenerCommonStore from '../../../../stores/screener/use-screener-common-store';
import { CHANNELS } from '../../../../shared/screener-constants';
import { PAGES } from '../../../../shared/screener-constants';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { isModel } from '../../../../utils/sharedHelpers';

const className = augmentClassName(styles);
export const FooterDisclaimers = ({
  distributionDisclaimers,
  footnoteDisclaimers,
  resultData,
  participantHoldings
}) => {
  const { commonState } = useScreenerCommonStore();
  const { channel } = commonState;
  const page = commonState.page;
  const [restrictionTextList, setRestrictionTextList] = useState([]);

  const holdingsByContext =
    resultData?.planHoldingsList?.length > 0 ? resultData?.planHoldingsList : participantHoldings;
  const isPlanInContext =
    channel === CHANNELS.MYTIAA ||
    channel === CHANNELS.DYNAMICMICROSITES ||
    resultData.isSearchedPlanInContext;
  const MStarNumbered = footnoteDisclaimersText?.MStarNumbered;
  const defaultBelowNumbered = footnoteDisclaimersText?.defaultBelowNumbered;
  const excludePrintingArray = [MStarNumbered, defaultBelowNumbered];

  const restrictedFundList = [];
  const updateRestrictedItems = () => {
    if (!resultData || !resultData?.screenerData?.length) {
      return;
    }
    let idIndex = 0;
    resultData?.screenerData.forEach((item, index) => {
      if (isModel(item.TCFundType)) {
        return;
      }
      const restrictionText = getRestrictionsText(
        item.TCWSODIssue,
        holdingsByContext,
        isPlanInContext
      );

      if (restrictionText && restrictionText.length > 0) {
        const joinedRestrictions = restrictionText?.join('<br></br>');
        const restrictedItem = {
          id: idIndex,
          displayName: `${item.TCInvestmentName} [${item.TCDerivedTicker}]`,
          text: joinedRestrictions
        };
        restrictedFundList.push(restrictedItem);
        idIndex++;
      }
    });
  };

  useEffect(() => {
    updateRestrictedItems();
    setRestrictionTextList(restrictedFundList);
  }, [resultData]);

  const renderDistributionsDisclosures = () => {
    return (
      <>
        {distributionDisclaimers.map((el, index) => {
          const disclaimer = format.stripHTML(el);
          return (
            <div
              data-key={`${(disclaimer || '').slice(0, 32)}-${index}`}
              key={`${(disclaimer || '').slice(0, 32)}-${index}`}
            >
              {parse(el)}
            </div>
          );
        })}
      </>
    );
  };

  const excludePrintingContent = contentText => {
    for (let index = 0; index < excludePrintingArray.length; index++) {
      if (contentText.includes(excludePrintingArray[index])) {
        return 'no-print';
      }
    }
    return '';
  };

  const renderScreenerDisclosures = () => {
    return (
      <>
        {footnoteDisclaimers.nonCollapsibleDisclaimers &&
          footnoteDisclaimers.nonCollapsibleDisclaimers.map((el, index) => {
            const disclaimer = format.stripHTML(el);
            return (
              <div
                data-key={`${(disclaimer || '').slice(0, 32)}-${index}`}
                key={`${(disclaimer || '').slice(0, 32)}-${index}`}
              >
                {parse(el)}
              </div>
            );
          })}
        <div
          {...className('show-all-disclaimers', 'screener-footer-disclaimers_show-all-disclaimers')}
        >
          <span aria-label="Show all disclaimers">Disclosures</span>
        </div>
        <div>
          {footnoteDisclaimers.defaultCollapsibleAboveNumbered &&
            parse(footnoteDisclaimers.defaultCollapsibleAboveNumbered[0])}
          <ol>
            {footnoteDisclaimers.numberedDisclaimers &&
              Object.values(footnoteDisclaimers.numberedDisclaimers).map((el, index) => {
                return (
                  <li
                    data-key={`${el.number || ''}-${index}`}
                    key={`${el.number || ''}-${index}`}
                    {...className(excludePrintingContent(el.text))}
                  >
                    {parse(el.text)}
                  </li>
                );
              })}
          </ol>

          {footnoteDisclaimers.collapsibleDisclaimers &&
            footnoteDisclaimers.collapsibleDisclaimers.map((el, index) => {
              const disclaimer = format.stripHTML(el);
              return (
                <div
                  data-key={`${(disclaimer || '').slice(0, 32)}-${index}`}
                  key={`${(disclaimer || '').slice(0, 32)}-${index}`}
                  {...className(excludePrintingContent(el))}
                >
                  {parse(el)}
                </div>
              );
            })}
        </div>
        <div {...className('print-only', 'restriction')}>
          {restrictionTextList.length > 0 && (
            <>
              <div {...className('restriction-disclosures-title')}>Restrictions</div>
              <p>
                {
                  'The following investments will not be available to receive new contributions (or transferred accumulations), but existing accumulations in the investment may remain invested in it:'
                }
              </p>
              {restrictionTextList.map((item, index) => {
                return (
                  <>
                    <span data-key={`${item.id || ''}-${index}`} key={`${item.id || ''}-${index}`}>
                      {item.displayName}
                      <span>{item.id < restrictionTextList.length - 1 ? ', ' : ''}</span>
                    </span>
                  </>
                );
              })}
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div {...className('py-5')}>
      {page === PAGES.SCREENER ? renderScreenerDisclosures() : renderDistributionsDisclosures()}
    </div>
  );
};

FooterDisclaimers.propTypes = {
  distributionDisclaimers: PropTypes.array,
  footnoteDisclaimers: PropTypes.object,
  resultData: PropTypes.object,
  participantHoldings: PropTypes.array
};

const mapStateToProps = state => {
  return {
    distributionDisclaimers: state.resultData.distributionInputs.distributionDisclaimers,
    footnoteDisclaimers: state.resultData.footnoteDisclaimers,
    resultData: state.resultData,
    participantHoldings: state.filterReducer.participantHoldings
  };
};

export default connect(mapStateToProps, null)(FooterDisclaimers);
